import React, { useEffect, useState, useRef, useImperativeHandle, useCallback } from "react";
import { Grid } from "@mui/material";
import {
	InvoiceByCarrierNo,
	InvoiceByCarrierType,
	InvoiceByDonate,
	InvoiceByEmail,
	InvoiceByIssuingType,
	InvoiceByManual,
	InvoiceByTriplicateUniform,
	useInvoiceFieldVisibility,
} from "@icoach/components/invoice";
import { refIsRequiredError } from "@util/utils";
import { INVOICE_CARRIER_TYPE, INVOICE_ISSUING_TYPE } from "@icoach/constants/dataEnums";

/**
 * 發票設定表單區塊
 */
const OrderInvoiceSection = React.forwardRef(({ className, sourceData, options = {}, errorModule = {}, readOnly }, ref) => {
	const { issuingType: issuingTypeProps, invoiceCarrierType: invoiceCarrierTypeProps, invoiceCarrierNo, donationCode, taxID, taxTitle, invoiceNo, issuingEmail } = sourceData || {};

	// 發票相關狀態
	const [issuingType, setIssuingType] = useState(issuingTypeProps || INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE);
	const [invoiceCarrierType, setInvoiceCarrierType] = useState(invoiceCarrierTypeProps || INVOICE_CARRIER_TYPE.MEMBERCARRIER);

	// 使用 useInvoiceFieldVisibility hook 管理欄位顯示邏輯
	const fieldVisibility = useInvoiceFieldVisibility({
		issuingType,
		invoiceCarrierType,
	});

	// 發票元件 refs
	const issuingTypeRef = useRef(null);
	const donateRef = useRef(null);
	const invoiceCarrierTypeRef = useRef(null);
	const invoiceCarrierNoRef = useRef(null);
	const triplicateUniformRef = useRef(null);
	const manualRef = useRef(null);
	const emailRef = useRef(null);

	// 處理發票類型變更
	const handleIssuingTypeChange = value => {
		setIssuingType(value);
		// 當發票類型變更時，重置載具類型為預設值
		if (value === INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE) {
			// 二聯式發票
			setInvoiceCarrierType(INVOICE_CARRIER_TYPE.MEMBERCARRIER);
		}
	};

	// 處理載具類型變更
	const handleInvoiceCarrierTypeChange = value => {
		setInvoiceCarrierType(value);
	};

	const getResult = useCallback(
		() => {
			let result = {};

			// 發票類型
			if (issuingTypeRef?.current?.getResult) {
				const { issuingType } = issuingTypeRef.current.getResult();
				result.issuingType = issuingType;
			}

			// 捐贈發票
			if (fieldVisibility.showDonateField && donateRef.current?.getResult) {
				const { donationCode } = donateRef.current.getResult();
				result.donationCode = donationCode;
			}

			// 載具類型
			if (fieldVisibility.showCarrierTypeField && invoiceCarrierTypeRef?.current?.getResult) {
				const { carrierType } = invoiceCarrierTypeRef.current.getResult();
				result.invoiceCarrierType = carrierType;
			}

			// 載具號碼
			if (fieldVisibility.showCarrierNoField && invoiceCarrierNoRef?.current?.getResult) {
				const { carrierNo } = invoiceCarrierNoRef.current.getResult();
				result.invoiceCarrierNo = carrierNo;
			}

			// 三聯式發票
			if (fieldVisibility.showTriplicateUniformField && triplicateUniformRef?.current?.getResult) {
				const { taxID, taxTitle } = triplicateUniformRef.current.getResult();
				result.taxID = taxID;
				result.taxTitle = taxTitle;
			}

			// 手開發票(發票號碼)
			if (fieldVisibility.showManualInvoiceField && manualRef?.current?.getResult) {
				const { invoiceNo } = manualRef.current.getResult();
				result.invoiceNo = invoiceNo;
			}

			// 發票通知信箱
			if (!fieldVisibility.showManualInvoiceField && emailRef?.current?.getResult) {
				const { issuingEmail } = emailRef.current.getResult();
				result.issuingEmail = issuingEmail;
			}

			return result;
		},
		// eslint-disable-next-line
		[fieldVisibility],
	);

	useEffect(
		() => {
			if (issuingTypeProps) setIssuingType(issuingTypeProps);
			if (invoiceCarrierTypeProps) setInvoiceCarrierType(invoiceCarrierTypeProps);
		},
		// eslint-disable-next-line
		[issuingTypeProps, invoiceCarrierTypeProps],
	);

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => refIsRequiredError(issuingTypeRef, donateRef, invoiceCarrierTypeRef, invoiceCarrierNoRef, triplicateUniformRef, manualRef, emailRef),
		}),
		// eslint-disable-next-line
		[getResult],
	);

	return (
		<Grid className={className} spacing={2} container>
			{/* 發票類型選擇 */}
			<Grid xs={12} item>
				<InvoiceByIssuingType
					ref={issuingTypeRef}
					issuingType={issuingType}
					options={options?.invoiceTypeOptions ?? []}
					errorData={errorModule}
					onChange={handleIssuingTypeChange}
					readOnly={readOnly}
					mode="select"
				/>
			</Grid>

			{/* 捐贈發票 */}
			{fieldVisibility.showDonateField && (
				<Grid xs={12} item>
					<InvoiceByDonate ref={donateRef} donationCode={donationCode} errorData={errorModule} readOnly={readOnly} mode="select" />
				</Grid>
			)}

			{/* 載具類型 */}
			{fieldVisibility.showCarrierTypeField && (
				<Grid xs={12} item>
					<InvoiceByCarrierType
						ref={invoiceCarrierTypeRef}
						carrierType={invoiceCarrierType}
						options={options?.invoiceCarrierTypeOptions ?? []}
						errorData={errorModule}
						onChange={handleInvoiceCarrierTypeChange}
						readOnly={readOnly}
					/>
				</Grid>
			)}

			{/* 載具號碼 */}
			{fieldVisibility.showCarrierNoField && (
				<Grid xs={12} item>
					<InvoiceByCarrierNo ref={invoiceCarrierNoRef} carrierNo={invoiceCarrierNo} carrierType={invoiceCarrierType} errorData={errorModule} readOnly={readOnly} />
				</Grid>
			)}

			{/* 三聯式發票 */}
			{fieldVisibility.showTriplicateUniformField && <InvoiceByTriplicateUniform ref={triplicateUniformRef} taxID={taxID} taxTitle={taxTitle} errorData={errorModule} readOnly={readOnly} />}

			{/* 手開發票 */}
			{fieldVisibility.showManualInvoiceField && (
				<Grid xs={12} item>
					<InvoiceByManual ref={manualRef} invoiceNo={invoiceNo} errorData={errorModule} readOnly={readOnly} />
				</Grid>
			)}

			{/* 發票通知信箱 */}
			{!fieldVisibility.showManualInvoiceField && (
				<Grid item xs={12}>
					<InvoiceByEmail
						ref={emailRef}
						issuingEmail={issuingEmail}
						InputProps={{ placeholder: "建議填寫，以便收取重要權益異動、發票、優惠等相關訊息" }}
						errorData={errorModule}
						readOnly={readOnly}
					/>
				</Grid>
			)}
		</Grid>
	);
});

export default OrderInvoiceSection;
